import { Modal } from 'ant-design-vue';
import type { ModalFuncProps } from 'ant-design-vue';

/** 操作确认文案 — 与 docs/operations/operation-flows.md 行为一致 */

export type NodeControlAction = 'restart' | 'stop' | 'upgrade';

export interface OperationHint {
  title: string;
  content: string;
  okText: string;
  okDanger?: boolean;
}

export const NODE_CONTROL_HINTS: Record<NodeControlAction, OperationHint> = {
  restart: {
    title: '确定重启服务器 Agent？',
    content: [
      '仅重启 supervisor 进程（systemctl restart）。',
      'SOCKS5 子进程通常不会中断（KillMode=process，重启后会 adopt 孤儿进程）。',
      '管理面心跳与连接统计会短暂中断约 5–15 秒。',
      '玩家一般无需重连。',
    ].join('\n'),
    okText: '重启',
  },
  stop: {
    title: '确定停止服务器 Agent？',
    content: [
      '终止全部 SOCKS5 子进程（该服务器上的所有节点连接会断开）。',
      '停止 supervisor 进程（systemctl stop）。',
      '管理面无法再控制该服务器；需在服务器上手动执行 systemctl start rxjh-agent 恢复。',
      '重新启动后 active 节点需等 join 才会自动 re-dispatch 重建。',
    ].join('\n'),
    okText: '停止',
    okDanger: true,
  },
  upgrade: {
    title: '确定升级服务器 Agent？',
    content: [
      '下载并安装新 agent binary。',
      '终止全部 SOCKS5 子进程（该服务器上的所有节点玩家需重连）。',
      '重启 supervisor 后自动 join admin。',
      'join 成功后，active 节点会自动 re-dispatch 重建。',
    ].join('\n'),
    okText: '升级',
  },
};

export const PROXY_RESTART_CHILD_HINT: OperationHint = {
  title: '确定重启该节点的子进程？',
  content: [
    '仅终止该端口的 SOCKS5 子进程。',
    '约 5–15 秒内 admin 收到 proxy_died 后自动 respawn 新 child。',
    '该节点上的玩家连接会断开，需重连；账号密码不变。',
  ].join('\n'),
  okText: '重启子进程',
  okDanger: true,
};

export const PROXY_DELETE_HINT: OperationHint = {
  title: '确定删除该节点？',
  content: [
    '数据库标记为 deleted，配置立即失效。',
    '若服务器在线，会通知 Agent 按端口 SIGTERM 终止 SOCKS5。',
    '若服务器离线，下次 join 时会补发 delete_proxy。',
    '删除后该端口可被新节点复用。',
  ].join('\n'),
  okText: '删除',
  okDanger: true,
};

export const NODE_DELETE_HINT: OperationHint = {
  title: '确定删除该服务器？',
  content: [
    '仅允许删除没有 active / creating 节点的服务器。',
    '删除会移除该服务器及其历史 expired / deleted 节点记录。',
    '若服务器 Agent 仍在运行，它会失去 Admin 管理，需要在机器上停用或重新安装后再加入。',
    '这个操作不可在页面中撤销。',
  ].join('\n'),
  okText: '删除服务器',
  okDanger: true,
};

export const REGIONS_SYNC_HINT: OperationHint = {
  title: '确定手动同步大区？',
  content: [
    '从内置 regions.json 读取最新大区 IP，与数据库比对并更新有变更的大区。',
    '不会删除已有大区，仅更新 domains / ips 字段。',
    '域名解析失败的条目会记录在同步历史中。',
  ].join('\n'),
  okText: '开始同步',
};

export const PROXY_EXTEND_OFFLINE_NOTE =
  '若节点当前为 offline / expired，续费仅更新数据库到期时间；服务器重新上线后才会生效。';

export function confirmOperation(
  hint: OperationHint,
  onOk: () => void | Promise<void>,
  extra?: Pick<ModalFuncProps, 'cancelText' | 'width'>,
): void {
  Modal.confirm({
    title: hint.title,
    content: hint.content,
    okText: hint.okText,
    cancelText: extra?.cancelText ?? '取消',
    width: extra?.width ?? 480,
    okButtonProps: hint.okDanger ? { danger: true } : undefined,
    onOk,
  });
}

export function confirmNodeControl(
  action: NodeControlAction,
  onOk: () => void | Promise<void>,
): void {
  confirmOperation(NODE_CONTROL_HINTS[action], onOk);
}
